import React, { useState, useEffect } from 'react';
import { Calculator, Sparkles, Loader2, Tag, Recycle, Store, CheckCircle2, Info } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const range = (r) => r ? `${eur(r.min)} – ${eur(r.max)}` : '—';

const lab = { fontSize: 12, fontWeight: 600, color: 'var(--cs-text-muted)', display: 'block', margin: '0 0 4px' };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid var(--cs-border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default function Waardebepaling() {
  const { t } = useT();
  const [parts, setParts] = useState([]);
  const [partId, setPartId] = useState('');
  const [d, setD] = useState({ naam: '', merk: '', model: '', bouwjaar: '', conditie: 'Gebruikt', oemNummer: '', motor: '', kmStand: '', afkomstigVoertuig: '', vraagPrijs: '' });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [applying, setApplying] = useState(false);
  const upd = (k, v) => setD(p => ({ ...p, [k]: v }));

  useEffect(() => { carsysApi.getParts({}).then(setParts).catch(() => {}); }, []);

  const pickPart = (id) => {
    setPartId(id);
    const p = parts.find(x => x.id === id);
    if (p) setD({
      naam: p.naam || '', merk: p.merk || '', model: p.model || '', bouwjaar: p.bouwjaar || '',
      conditie: p.conditie || 'Gebruikt', oemNummer: p.oemNummer || '', motor: p.motor || '',
      kmStand: p.kmStand || '', afkomstigVoertuig: p.afkomstigVoertuig || '', vraagPrijs: p.vraagPrijs || '',
    });
    setRes(null);
  };

  const bepaal = async () => {
    if (!d.naam.trim()) { toast.error(t('Vul een onderdeelnaam in', 'Vul een onderdeelnaam in')); return; }
    setBusy(true); setRes(null);
    try { setRes(await carsysApi.partWaardebepaling(d)); }
    catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const pasToe = async () => {
    if (!partId || !res?.adviesVraagprijs) return;
    setApplying(true);
    try {
      await carsysApi.updatePart(partId, { vraagPrijs: Number(res.adviesVraagprijs) });
      toast.success(t('Vraagprijs bijgewerkt', 'Vraagprijs bijgewerkt'), { description: eur(res.adviesVraagprijs) });
    } catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setApplying(false); }
  };

  return (
    <div>
      <div className="cs-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Calculator size={20} /></div>
          <div><h2>{t('Waardebepaling', 'Waardebepaling')}</h2><p>{t('AI bepaalt nieuwprijs, gebruikte marktwaarde en sloopwaarde', 'AI bepaalt nieuwprijs, gebruikte marktwaarde en sloopwaarde')}</p></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: 18, alignItems: 'start' }}>
        {/* Invoer */}
        <div className="cs-card padded">
          <label style={lab}>{t('Kies onderdeel uit voorraad', 'Kies onderdeel uit voorraad')}</label>
          <select style={{ ...inp, marginBottom: 12 }} value={partId} onChange={e => pickPart(e.target.value)} data-testid="wb-part-select">
            <option value="">— {t('Of vul handmatig in', 'Of vul handmatig in')} —</option>
            {parts.map(p => <option key={p.id} value={p.id}>{p.naam}{p.afkomstigVoertuig ? ` · ${p.afkomstigVoertuig}` : ''}</option>)}
          </select>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ gridColumn: '1 / 3' }}><label style={lab}>{t('Onderdeelnaam', 'Onderdeelnaam')} *</label><input style={inp} value={d.naam} onChange={e => upd('naam', e.target.value)} data-testid="wb-naam" /></div>
            <div><label style={lab}>{t('Merk', 'Merk')}</label><input style={inp} value={d.merk} onChange={e => upd('merk', e.target.value)} /></div>
            <div><label style={lab}>{t('Model', 'Model')}</label><input style={inp} value={d.model} onChange={e => upd('model', e.target.value)} /></div>
            <div><label style={lab}>{t('Bouwjaar', 'Bouwjaar')}</label><input style={inp} value={d.bouwjaar} onChange={e => upd('bouwjaar', e.target.value)} /></div>
            <div><label style={lab}>{t('Conditie', 'Conditie')}</label>
              <select style={inp} value={d.conditie} onChange={e => upd('conditie', e.target.value)}>
                {['Nieuw', 'Zo goed als nieuw', 'Gebruikt', 'Refurbished', 'Defect / voor onderdelen'].map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label style={lab}>OEM-nr</label><input style={inp} value={d.oemNummer} onChange={e => upd('oemNummer', e.target.value)} /></div>
            <div><label style={lab}>{t('Motor / inhoud', 'Motor / inhoud')}</label><input style={inp} value={d.motor} onChange={e => upd('motor', e.target.value)} /></div>
          </div>
          <button className="cs-btn primary" style={{ width: '100%', marginTop: 14, background: '#7c3aed', borderColor: '#7c3aed' }} onClick={bepaal} disabled={busy} data-testid="wb-bepaal">
            {busy ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />} {t('Bepaal waarde', 'Bepaal waarde')}
          </button>
        </div>

        {/* Resultaat */}
        <div>
          {!res && !busy && (
            <div className="cs-card padded" style={{ textAlign: 'center', color: 'var(--cs-text-muted)', padding: 48 }}>
              <Calculator size={40} style={{ opacity: 0.3 }} />
              <p style={{ marginTop: 12 }}>{t('Vul een onderdeel in en klik op "Bepaal waarde" voor een AI-schatting.', 'Vul een onderdeel in en klik op "Bepaal waarde" voor een AI-schatting.')}</p>
            </div>
          )}
          {busy && <div className="cs-card padded" style={{ textAlign: 'center', padding: 48 }}><Loader2 size={32} className="spin" style={{ color: '#7c3aed' }} /><p style={{ marginTop: 10, color: 'var(--cs-text-muted)' }}>{t('AI taxeert…', 'AI taxeert…')}</p></div>}
          {res && (
            <div data-testid="wb-result">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <WaardeCard icon={<Tag size={18} />} kleur="#2563eb" label={t('Nieuwprijs', 'Nieuwprijs')} value={range(res.nieuwprijs)} />
                <WaardeCard icon={<Store size={18} />} kleur="#16a34a" label={t('Gebruikte marktwaarde', 'Gebruikte marktwaarde')} value={range(res.gebruikteMarktwaarde)} testid="wb-marktwaarde" />
                <WaardeCard icon={<Recycle size={18} />} kleur="#d97706" label={t('Sloopwaarde', 'Sloopwaarde')} value={range(res.sloopwaarde)} />
              </div>

              <div className="cs-card padded" style={{ marginTop: 14, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                <div><div style={lab}>{t('Advies-vraagprijs', 'Advies-vraagprijs')}</div><div style={{ fontSize: 26, fontWeight: 800, color: '#7c3aed' }} data-testid="wb-advies">{eur(res.adviesVraagprijs)}</div></div>
                <div><div style={lab}>{t('Snelle-verkoopprijs', 'Snelle-verkoopprijs')}</div><div style={{ fontSize: 20, fontWeight: 700 }}>{eur(res.snelleVerkoopPrijs)}</div></div>
                {partId && (
                  <button className="cs-btn primary" style={{ marginLeft: 'auto' }} onClick={pasToe} disabled={applying} data-testid="wb-pas-toe">
                    {applying ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />} {t('Toepassen op onderdeel', 'Toepassen op onderdeel')}
                  </button>
                )}
              </div>

              {res.toelichting && <p style={{ marginTop: 14, color: 'var(--cs-text)', lineHeight: 1.6 }}>{res.toelichting}</p>}
              {res.let_op && <div style={{ marginTop: 8, fontSize: 12, color: '#b45309', display: 'flex', gap: 6, alignItems: 'center' }}><Info size={14} /> {res.let_op}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const WaardeCard = ({ icon, kleur, label, value, testid }) => (
  <div className="cs-card padded" data-testid={testid} style={{ borderTop: `3px solid ${kleur}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: kleur, fontWeight: 700, fontSize: 12.5 }}>{icon} {label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, marginTop: 8, color: 'var(--cs-text)' }}>{value}</div>
  </div>
);
